import React, { useCallback, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Divider, MenuItem, Select, Stack, Typography } from '@mui/material';

import { CatalogRoom } from '~/types/stopsell';

interface Props<T extends { catalogRoomId: string }> {
  catalogRooms: Array<CatalogRoom>;
  extractedRoomTypeIds: Set<string>;
  manualRooms: Array<T>;
  onAddRoom: (catalogRoomId: string, catalogRoomName: string) => void;
  renderCard: (room: T, roomIndex: number) => React.ReactNode;
}

export default function AvailabilityManualRoomsSection<T extends { catalogRoomId: string }>({
  catalogRooms,
  extractedRoomTypeIds,
  manualRooms,
  onAddRoom,
  renderCard,
}: Props<T>) {
  const [selectedRoomId, setSelectedRoomId] = useState('');

  const availableRooms = useMemo(
    () =>
      catalogRooms.filter(
        (room) => !extractedRoomTypeIds.has(room.id) && !manualRooms.some((m) => m.catalogRoomId === room.id),
      ),
    [catalogRooms, extractedRoomTypeIds, manualRooms],
  );

  const handleAddRoom = useCallback(() => {
    const room = availableRooms.find((r) => r.id === selectedRoomId);
    if (!room) return;
    onAddRoom(room.id, room.name);
    setSelectedRoomId('');
  }, [availableRooms, selectedRoomId, onAddRoom]);

  return (
    <Box>
      <Divider sx={{ borderStyle: 'dashed', my: 2 }} />
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <AddIcon fontSize="small" />
        <Typography fontWeight={500}>Add Missing Rooms</Typography>
      </Stack>

      <Stack direction="row" gap={1} mb={2}>
        <Select
          size="small"
          displayEmpty
          value={selectedRoomId}
          onChange={(e) => setSelectedRoomId(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="" disabled>
            Select a room...
          </MenuItem>
          {availableRooms.map((room) => (
            <MenuItem key={room.id} value={room.id}>
              {room.name}
            </MenuItem>
          ))}
        </Select>
        <Button variant="contained" disabled={!selectedRoomId} onClick={handleAddRoom}>
          Add Room
        </Button>
      </Stack>

      <Stack gap={2}>
        {manualRooms.map((room, roomIndex) => (
          <React.Fragment key={room.catalogRoomId}>{renderCard(room, roomIndex)}</React.Fragment>
        ))}
      </Stack>
    </Box>
  );
}
